import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  AgFilterUtils,
  AgGridPaginationStatusBar,
  buildGridOptions,
  exportGridAsExcel,
  Page,
  PAGE_DEFAULT,
  PaginationStatus,
} from '@gestor-web/app/shared/components';
import { CpfCnpjPipe } from '@gestor-web/app/shared/pipes/cpf-ou-cnpj.pipe';
import { NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { AgGridEvent, ColDef, GridOptions } from 'ag-grid-community';
import { BehaviorSubject, Subject, combineLatest, forkJoin, of } from 'rxjs';
import { catchError, filter, first, takeUntil } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { SelectItem } from 'primeng/api';
import { GuiasComPacoteService } from './guias-com-pacote.service';
import { GuiaComPacote } from '@gestor-web/@core/api/ords/model/guias-com-pacote';
import { AgGridActionCellParams, AgGridActionCellRenderer } from '@gestor-web/app/shared/components';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

const GRID_KEY = 'GUIAS_COM_PACOTE';
const PAGE_TOOLTIP = 'RELALTORIOS_PERSONALIZADOS.GUIAS_COM_PACOTE';

const CODIGO_PACOTE_OPTIONS: SelectItem[] = [
  { label: 'Código pacote', value: null },
  { label: '90 - Pacote odontológico', value: '90' },
  { label: '98 - Pacote geral', value: '98' },
];

const PROCED_ITEM_PLACEHOLDER: SelectItem = { label: 'Cód. procedimento', value: null };

const COLUMNS: ColDef[] = [
  {
    headerName: 'Cód. lancto',
    field: 'cd_guia_monitor',
    width: 160,
    cellRenderer: AgGridActionCellRenderer,
    cellRendererParams: {
      actionTooltip: 'Exibir detalhes da guia',
    } as AgGridActionCellParams,
    type: 'str',
  },
  {
    headerName: '006-Registro ANS',
    field: 'cd_registro_ans', 
    hide: true 
  },
  { 
    headerName: '024-Nr. guia operadora',
    field: 'nr_guia_operadora', 
    width: 190 
  },
  { 
    headerName: '023-Nr. guia prestador',
    field: 'nr_guia_prestador', 
    width: 190 
  },
  { 
    headerName: '014-CNPJ/CPF executante',
    field: 'nr_cnpj_cpf_exec', 
    type: 'cpfCnpj', 
    width: 210 
  },
  { 
    headerName: 'Origem da guia', 
    field: 'origem_guia', 
    width: 220 
  },
  { 
    headerName: 'Cód. Tabela TUSS', 
    field: 'ds_tabela_tuss', 
    width: 200 
  },
  { 
    headerName: 'Cód. procedimento', 
    field: 'cd_proced_item', 
    width: 180 
  },
  { 
    headerName: 'Detalhe do pacote', 
    field: 'lt_detalhe_pacote', 
    width: 180 
  },
  {
    headerName: 'Qt pago pacote',
    field: 'qt_pago_pacote',
    type: 'money',
    aggFunc: 'sum',
    width: 180,
  },
  {
    headerName: 'Ib proced. elegível',
    field: 'ib_proced_elegivel',
    width: 190,
    valueFormatter: params => (params.value === 1 ? 'Sim' : 'Não'),
  },
];

@Component({
    templateUrl: './guias-com-pacote.component.html',
    styleUrls: ['./guias-com-pacote.component.scss'],
    providers: [GuiasComPacoteService, DatePipe, DecimalPipe, CpfCnpjPipe],
    standalone: false
})
export class GuiasComPacoteComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;
  pageTooltip = PAGE_TOOLTIP;

  @ViewChild('agGrid') agGrid: AgGridAngular<GuiaComPacote>;

  gridOptions: GridOptions<GuiaComPacote>;

  page: number = PAGE_DEFAULT.page;
  pageSize: number = PAGE_DEFAULT.limit;
  pageData: Page = PAGE_DEFAULT;
  paginationReset = false;
  pagination$: BehaviorSubject<PaginationStatus> = new BehaviorSubject<PaginationStatus>(null);

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;
  competenciaRange: string[];

  cdTabelaTuss: string | null = null;
  cdProcedItem: string | null = null;
  cdTabelaTussOptions: SelectItem[] = CODIGO_PACOTE_OPTIONS;
  cdProcedItemOptions: SelectItem[] = [PROCED_ITEM_PLACEHOLDER];
  filtrosProcedimentoLoading = false;

  rowData: GuiaComPacote[] = [];
  rowTotal: number = 0;
  dtUltimoProcessamento: string = null;

  guiasList: string[] = [];

  detalheGuiaDisplay = false;
  detalheGuiaCodigo: string;
  detalheGuiaCodigoIndex: number;

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  constructor(
    private permissaoService: PermissaoService,
    private service: GuiasComPacoteService,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
    private cpfCnpjPipe: CpfCnpjPipe,
    private notificationService: NotificationService,
    private gridService: GridService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen).pipe(takeUntil(this.ngDestroyed$));
    this.gridHeight$ = this.fullscreen$.pipe(
      map(fullscreen => (fullscreen ? 'calc(100vh - 178px)' : 'calc(100vh - 233px)')),
    );

    const ano = new Date().getFullYear();
    this.competenciaRange = [`${ano}01`, `${ano}12`];

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(([operadora, params]) => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;
        this.competenciaRange = [
          params.competencia ? '' + params.competencia : null,
          params.competencia_final ? '' + params.competencia_final : null,
        ];

        this.reloadProcedimentosDisponiveis();
        setTimeout(() => this.pageReady$.next());
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter]) => {
        if (!this.firstLoad || hasFilter) {
          this.reloadData();
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    const columnDefs = COLUMNS;
    const colGuiaMonitor = columnDefs.find(col => col.field === 'cd_guia_monitor');

    colGuiaMonitor.cellRendererParams['actionFunction'] = (value: string) => {
      this.showDetalheGuiaFor(value);
    };

    this.gridOptions = buildGridOptions<GuiaComPacote>(
      {
        columnDefs,
        pipes: {
          decimal: this.decimalPipe,
          cpfCnpj: this.cpfCnpjPipe,
        },
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
      },
      {
        onFirstDataRendered: event => this.updateGuiasList(event),
        onFilterChanged: event => this.updateGuiasList(event),
        onSortChanged: event => this.updateGuiasList(event),
        onRowDataUpdated: event => this.updateGuiasList(event),
        statusBar: {
          statusPanels: [
            {
              statusPanel: AgGridPaginationStatusBar,
              statusPanelParams: { pagination$: this.pagination$ },
              align: 'left',
            },
          ],
        },
      },
    );
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, cd_tabela_tuss, cd_proced_item, sort, extraFilters } = params;
    let hasFilter = false;

    if (competencia) {
      hasFilter = true;
      this.competenciaRange[0] = competencia;
    }
    if (competencia_final) {
      hasFilter = true;
      this.competenciaRange[1] = competencia_final;
    }
    if (cd_tabela_tuss) {
      hasFilter = true;
      this.cdTabelaTuss = cd_tabela_tuss;
    }
    if (cd_proced_item) {
      hasFilter = true;
      this.cdProcedItem = cd_proced_item;
    }
    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, null, sort, filterModel));

    this.router.navigate([], { relativeTo: this.route, queryParams: {} });

    this.filterReady$.next(hasFilter);
  }

  private reloadProcedimentosDisponiveis(): void {
    if (!this.operadora || !this.competenciaRange[0] || !this.competenciaRange[1]) {
      this.cdProcedItemOptions = [PROCED_ITEM_PLACEHOLDER];
      return;
    }

    this.filtrosProcedimentoLoading = true;
    this.service
      .fetchProcedimentosDisponiveis(
        this.operadora,
        this.competenciaRange[0],
        this.competenciaRange[1],
        this.cdTabelaTuss,
      )
      .subscribe({
        next: items => {
          this.cdProcedItemOptions = [
            PROCED_ITEM_PLACEHOLDER,
            ...items.map(item => ({ label: item.cd_proced_item, value: item.cd_proced_item })),
          ];

          if (this.cdProcedItem && !this.cdProcedItemOptions.some(opt => opt.value === this.cdProcedItem)) {
            this.cdProcedItem = null;
          }
        },
        error: error => this.notificationService.handleError(error),
        complete: () => (this.filtrosProcedimentoLoading = false),
      });
  }

  onCodigoPacoteChange(): void {
    this.reloadProcedimentosDisponiveis();
    this.onFilterChange();
  }

  onFilterChange(): void {
    this.filterChanged = true;
    this.page = 0;
    this.pageData = { ...this.pageData, page: 0, size: this.pageSize, offset: 0, limit: this.pageSize };
  }

  competenciaChanged(range: string[]) {
    this.competenciaRange = range;
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );
    this.reloadProcedimentosDisponiveis();
    this.onFilterChange();
  }

  reloadData() {
    this.filterChanged = false;

    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => this.agGrid?.api?.setGridOption('loading', true));
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    if (!this.competenciaRange[0] || !this.competenciaRange[1]) {
      this.rowData = [];
      this.rowTotal = 0;
      this.dtUltimoProcessamento = null;
      this.agGrid?.api?.setGridOption('loading', false);
      this.paginationReset = false;
      this.pagination$.next({ ...this.pageData, endOffset: this.rowTotal });
      return;
    }

    this.pageData = {
      page: this.page,
      size: this.pageSize,
      offset: this.page * this.pageSize,
      limit: this.pageSize,
    };

    const grid$ = this.service.fetchData(
      this.operadora,
      this.competenciaRange[0],
      this.competenciaRange[1],
      this.cdTabelaTuss,
      this.cdProcedItem,
      this.pageData,
    );

    const ultimo$ = this.service.getUltimoProcessamento(this.operadora).pipe(catchError(() => of(null)));

    forkJoin({ grid: grid$, ultimo: ultimo$ }).subscribe({
      next: ({ grid, ultimo }) => {
        this.rowData = grid.items;
        this.rowTotal = grid.total;
        this.dtUltimoProcessamento = this.getDataUltimoProcessamento(ultimo);
        this.paginationReset = false;
        this.pagination$.next({ ...this.pageData, endOffset: this.rowTotal });
      },
      error: error => this.notificationService.handleError(error),
      complete: () => this.agGrid?.api?.setGridOption('loading', false),
    });
  }

  private getDataUltimoProcessamento(
    ultimoProcessamentoResponse: { items?: Array<{ dt_atualizacao_mon_guia?: string | number }> } | null,
  ): string {
    const valor = ultimoProcessamentoResponse?.items?.[0]?.dt_atualizacao_mon_guia;
    if (!valor) return null;

    const timestamp = Number(valor);
    return this.datePipe.transform(Number.isNaN(timestamp) ? valor : timestamp, 'dd/MM/yyyy');
  }

  private updateGuiasList(event: AgGridEvent<GuiaComPacote>) {
    const guias = new Set<string>();
    event.api.forEachNodeAfterFilterAndSort(node => {
      if (node.data?.cd_guia_monitor) {
        guias.add(node.data.cd_guia_monitor);
      }
    });
    this.guiasList = Array.from(guias);
  }

  showDetalheGuiaFor(cd_guia_monitor: string) {
    this.detalheGuiaDisplay = true;
    this.detalheGuiaCodigo = cd_guia_monitor;
    this.detalheGuiaCodigoIndex = this.guiasList.indexOf(cd_guia_monitor);
  }

  showDetalheGuiaForNext() {
    if (this.detalheGuiaCodigoIndex > this.guiasList.length - 2) return;
    this.detalheGuiaCodigoIndex++;
    this.detalheGuiaCodigo = this.guiasList[this.detalheGuiaCodigoIndex];
  }

  showDetalheGuiaForPrev() {
    if (this.detalheGuiaCodigoIndex < 1) return;
    this.detalheGuiaCodigoIndex--;
    this.detalheGuiaCodigo = this.guiasList[this.detalheGuiaCodigoIndex];
  }

  onPageChange(page: Page) {
    this.pageData = page;
    this.page = page.page;
    this.pageSize = page.size;

    this.reloadData();
  }

  onSearch(value: string) {
    this.agGrid?.api?.updateGridOptions({ quickFilterText: value });
  }

  exportExcel(): void {
    const fileNamePrefix = `Guias-Com-Pacote-${this.operadoraNome}-${this.competenciaRange[0]}-${this.competenciaRange[1]}`;
    const headers = [
      'Guias com pacote de códigos elegíveis',
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
    ];
    exportGridAsExcel({ api: this.agGrid.api, headers, fileNamePrefix });
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  onCopyUrlFilters() {
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);

    const filter = {
      competencia: this.competenciaRange[0],
      competencia_final: this.competenciaRange[1],
      cd_tabela_tuss: this.cdTabelaTuss,
      cd_proced_item: this.cdProcedItem,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}